import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingUp, CheckCircle2 } from 'lucide-react';
function AnimatedCounter({
  end,
  duration = 2,
  prefix = '',
  suffix = '',
  isCurrency = false
}: {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  isCurrency?: boolean;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (isInView) {
      let startTime: number;
      let animationFrame: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = timestamp - startTime;
        const percentage = Math.min(progress / (duration * 1000), 1);
        // Easing function (easeOutExpo)
        const easeOut = percentage === 1 ? 1 : 1 - Math.pow(2, -10 * percentage);
        setCount(Math.floor(end * easeOut));
        if (percentage < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };
      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [end, duration, isInView]);
  const formattedCount = isCurrency ? count.toLocaleString() : count.toLocaleString();
  return <span ref={ref} data-id="element-284">
      {prefix}
      {formattedCount}
      {suffix}
    </span>;
}
export function MarketStats() {
  return <section className="py-24 bg-charcoal px-6" data-id="element-285">
      <div className="max-w-7xl mx-auto" data-id="element-286">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-287">
          <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-288">
            Market Insights
          </span>
          <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-289">
            Las Vegas Market{' '}
            <span className="italic text-gold" data-id="element-290">at a Glance</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12" data-id="element-291">
          {/* Stat 1 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-center border-l border-white/10 pl-4 py-2" data-id="element-292">
            <div className="text-4xl md:text-5xl font-serif text-white mb-2" data-id="element-293">
              <AnimatedCounter end={450000} prefix="$" isCurrency={true} data-id="element-294" />
            </div>
            <div className="text-white/60 font-light text-sm uppercase tracking-wider" data-id="element-295">
              Median Home Price
            </div>
          </motion.div>

          {/* Stat 2 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-center border-l border-white/10 pl-4 py-2" data-id="element-296">
            <div className="text-4xl md:text-5xl font-serif text-white mb-2 flex items-center justify-center gap-2" data-id="element-297">
              <TrendingUp className="w-6 h-6 text-green-400" data-id="element-298" />
              <AnimatedCounter end={5.2} suffix="%" data-id="element-299" />
            </div>
            <div className="text-white/60 font-light text-sm uppercase tracking-wider" data-id="element-300">
              YoY Appreciation
            </div>
          </motion.div>

          {/* Stat 3 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-center border-l border-white/10 pl-4 py-2" data-id="element-301">
            <div className="text-4xl md:text-5xl font-serif text-white mb-2" data-id="element-302">
              <AnimatedCounter end={28} data-id="element-303" />
            </div>
            <div className="text-white/60 font-light text-sm uppercase tracking-wider" data-id="element-304">
              Avg Days on Market
            </div>
          </motion.div>

          {/* Stat 4 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="text-center border-l border-white/10 pl-4 py-2" data-id="element-305">
            <div className="text-4xl md:text-5xl font-serif text-white mb-2" data-id="element-306">
              <AnimatedCounter end={42000} suffix="+" isCurrency={true} data-id="element-307" />
            </div>
            <div className="text-white/60 font-light text-sm uppercase tracking-wider" data-id="element-308">
              Homes Sold (2024)
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12 pt-12 border-t border-white/10" data-id="element-309">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="flex items-center justify-center gap-3 text-white/80" data-id="element-310">
            <CheckCircle2 className="w-5 h-5 text-gold" data-id="element-311" />
            <span className="font-light text-lg" data-id="element-312">No State Income Tax</span>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="flex items-center justify-center gap-3 text-white/80" data-id="element-313">
            <TrendingUp className="w-5 h-5 text-gold" data-id="element-314" />
            <span className="font-light text-lg" data-id="element-315">
              +2.1% Annual Population Growth
            </span>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="text-center mt-16" data-id="element-316">
          <p className="text-xs text-white/40 font-light" data-id="element-317">
            Las Vegas Metro Area — Source: Las Vegas Realtors Association, 2024
          </p>
        </motion.div>
      </div>
    </section>;
}